import React, { useState } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Row, 
  Col,
  Typography,
  message,
  Popconfirm,
  Tooltip,
  DatePicker,
  InputNumber,
  Badge,
  Tabs
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CarOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import dayjs from 'dayjs';
import type { CustomFieldConfig } from '../../services/missionTypeService';
import CustomFieldRenderer from '../../components/CustomFieldRenderer';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface Mission {
  id: string;
  mission_type_id: string;
  driver_id?: string;
  vehicle_id?: string;
  title: string;
  description?: string;
  priority: 0 | 1 | 2;
  pickup_address?: string;
  pickup_latitude?: number;
  pickup_longitude?: number;
  delivery_address?: string;
  delivery_latitude?: number;
  delivery_longitude?: number;
  scheduled_pickup?: string;
  scheduled_delivery?: string;
  estimated_duration?: number;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  actual_pickup?: string;
  actual_delivery?: string;
  custom_data: Record<string, any>;
  notes?: string;
  created_at: string;
  updated_at: string;
  mission_type_name: string;
  driver_license?: string;
  driver_first_name?: string;
  driver_last_name?: string;
  vehicle_license_plate?: string;
}

interface MissionType {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  custom_fields: CustomFieldConfig[];
}

interface MissionFormData {
  mission_type_id: string;
  driver_id?: string;
  vehicle_id?: string;
  title: string;
  description?: string;
  priority: 0 | 1 | 2;
  pickup_address?: string;
  delivery_address?: string;
  scheduled_pickup?: string;
  scheduled_delivery?: string;
  estimated_duration?: number;
  custom_data?: Record<string, any>;
  notes?: string;
}

const AdminMissions: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMission, setEditingMission] = useState<Mission | null>(null);
  const [selectedMissionType, setSelectedMissionType] = useState<MissionType | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Récupération des missions
  const { data: missionsData, isLoading } = useQuery({
    queryKey: ['missions', searchText, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (statusFilter) params.append('status', statusFilter);

      const response = await apiClient.get(`/admin/missions?${params.toString()}`);
      return response.data;
    },
  });

  // Récupération des types de missions
  const { data: missionTypesData } = useQuery({
    queryKey: ['mission-types'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/mission-types');
      return response.data;
    },
  });

  // Récupération des chauffeurs disponibles
  const { data: driversData } = useQuery({
    queryKey: ['available-drivers'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/drivers');
      return response.data;
    },
  });

  // Récupération des véhicules disponibles
  const { data: vehiclesData } = useQuery({
    queryKey: ['available-vehicles-missions'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/vehicles?status=available');
      return response.data;
    },
  });

  const missions: Mission[] = missionsData?.missions || [];
  const missionTypes: MissionType[] = missionTypesData?.mission_types || [];
  const availableDrivers = driversData?.drivers || [];
  const availableVehicles = vehiclesData?.vehicles || [];

  // Mutation pour créer une mission
  const createMissionMutation = useMutation({
    mutationFn: async (data: MissionFormData) => {
      const response = await apiClient.post('/admin/missions', data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Mission créée avec succès');
      setIsModalVisible(false);
      form.resetFields();
      setSelectedMissionType(null);
      queryClient.invalidateQueries({ queryKey: ['missions'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la création');
    },
  });

  // Mutation pour modifier une mission
  const updateMissionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<MissionFormData> }) => {
      const response = await apiClient.put(`/admin/missions/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Mission modifiée avec succès');
      setIsModalVisible(false);
      setEditingMission(null);
      form.resetFields();
      setSelectedMissionType(null);
      queryClient.invalidateQueries({ queryKey: ['missions'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la modification');
    },
  });

  // Mutation pour supprimer une mission
  const deleteMissionMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/missions/${id}`);
    },
    onSuccess: () => {
      message.success('Mission supprimée avec succès');
      queryClient.invalidateQueries({ queryKey: ['missions'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la suppression');
    },
  });

  // Gestionnaires d'événements
  const handleCreate = () => {
    setEditingMission(null);
    setSelectedMissionType(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (mission: Mission) => {
    setEditingMission(mission);
    const missionType = missionTypes.find(mt => mt.id === mission.mission_type_id);
    setSelectedMissionType(missionType || null);

    form.setFieldsValue({
      ...mission,
      scheduled_pickup: mission.scheduled_pickup ? dayjs(mission.scheduled_pickup) : null,
      scheduled_delivery: mission.scheduled_delivery ? dayjs(mission.scheduled_delivery) : null,
      mission_data: mission.custom_data || {},
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMissionMutation.mutate(id);
  };

  const handleSubmit = (values: any) => {
    const formattedData = {
      ...values,
      scheduled_pickup: values.scheduled_pickup ? values.scheduled_pickup.format() : null,
      scheduled_delivery: values.scheduled_delivery ? values.scheduled_delivery.format() : null,
    };

    if (editingMission) {
      updateMissionMutation.mutate({ id: editingMission.id, data: formattedData });
    } else {
      createMissionMutation.mutate(formattedData);
    }
  };

  const handleMissionTypeChange = (missionTypeId: string) => {
    const missionType = missionTypes.find(mt => mt.id === missionTypeId);
    setSelectedMissionType(missionType || null);
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'orange',
      assigned: 'blue',
      in_progress: 'purple',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  // Fonction pour obtenir le texte du statut
  const getStatusText = (status: string) => {
    const texts = {
      pending: 'En attente',
      assigned: 'Assignée',
      in_progress: 'En cours',
      completed: 'Terminée',
      cancelled: 'Annulée',
    };
    return texts[status as keyof typeof texts] || status;
  };

  // Fonction pour obtenir la couleur de priorité
  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'low': return 'Basse';
      case 'medium': return 'Normal';
      case 'high': return 'Élevée';
      case 'urgent': return 'Urgente';
      default: return 'Normal';
    }
  };
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'green';
      case 'medium': return 'blue';
      case 'high': return 'orange';
      case 'urgent': return 'red';
      default: return 'blue';
    }
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Mission',
      key: 'mission',
      render: (record: Mission) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <FileTextOutlined style={{ marginRight: '8px' }} />
            {record.title}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.mission_type_name}
          </Text>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              {record.description.length > 50 ? 
                `${record.description.substring(0, 50)}...` : 
                record.description
              }
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      filters: [
        { text: 'En attente', value: 'pending' },
        { text: 'Assignée', value: 'assigned' },
        { text: 'En cours', value: 'in_progress' },
        { text: 'Terminée', value: 'completed' },
        { text: 'Annulée', value: 'cancelled' },
      ],
    },
    {
      title: 'Priorité',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityText(priority)}
        </Tag>
      ),
      sorter: (a: Mission, b: Mission) => b.priority - a.priority,
    },
    {
      title: 'Chauffeur',
      key: 'driver',
      render: (record: Mission) => (
        record.driver_first_name ? (
          <div>
            <Badge status="success" />
            <div style={{ fontWeight: 'bold' }}>
              <UserOutlined style={{ marginRight: '4px' }} />
              {record.driver_first_name} {record.driver_last_name}
            </div>
            {record.driver_license && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Permis: {record.driver_license}
              </div>
            )}
          </div>
        ) : (
          <Text type="secondary">
            <Badge status="default" />
            Non assigné
          </Text>
        )
      ),
    },
    {
      title: 'Véhicule',
      key: 'vehicle',
      render: (record: Mission) => (
        record.vehicle_license_plate ? (
          <div>
            <Badge status="success" />
            <div style={{ fontWeight: 'bold' }}>
              <CarOutlined style={{ marginRight: '4px' }} />
              {record.vehicle_license_plate}
            </div>
          </div>
        ) : (
          <Text type="secondary">
            <Badge status="default" />
            Non assigné
          </Text>
        )
      ),
    },
    {
      title: 'Plannification',
      key: 'schedule',
      render: (record: Mission) => (
        <div>
          {record.scheduled_pickup && (
            <div style={{ fontSize: '12px', marginBottom: '2px' }}>
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              Départ: {dayjs(record.scheduled_pickup).format('DD/MM HH:mm')}
            </div>
          )}
          {record.scheduled_delivery && (
            <div style={{ fontSize: '12px' }}>
              Arrivée: {dayjs(record.scheduled_delivery).format('DD/MM HH:mm')}
            </div>
          )}
          {record.estimated_duration && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              Durée: {record.estimated_duration} min
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Créée le',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a: Mission, b: Mission) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Mission) => (
        <Space>
          <Tooltip title="Modifier">
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer cette mission ?"
              description="Cette action est irréversible."
              onConfirm={() => handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Gestion des Missions</Title>
        <p style={{ color: '#666', margin: 0 }}>
          Créez et gérez les missions de transport
        </p>
      </div>

      {/* Filtres et actions */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Rechercher une mission..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
            >
              <Option value="">Tous les statuts</Option>
              <Option value="pending">En attente</Option>
              <Option value="assigned">Assignée</Option>
              <Option value="in_progress">En cours</Option>
              <Option value="completed">Terminée</Option>
              <Option value="cancelled">Annulée</Option>
            </Select>
          </Col>
          <Col xs={24} sm={10} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouvelle mission
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau des missions */}
      <Card>
        <Table
          columns={columns}
          dataSource={missions}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} sur ${total} missions`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Modal de création/modification */}
      <Modal
        title={editingMission ? 'Modifier la mission' : 'Nouvelle mission'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingMission(null);
          setSelectedMissionType(null);
          form.resetFields();
        }}
        footer={null}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Tabs defaultActiveKey="basic">
            <TabPane tab="Informations de base" key="basic">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="mission_type_id"
                    label="Type de mission"
                    rules={[{ required: true, message: 'Le type de mission est requis' }]}
                  >
                    <Select
                      placeholder="Sélectionner un type"
                      onChange={handleMissionTypeChange}
                    >
                      {missionTypes.map(type => (
                        <Option key={type.id} value={type.id}>
                          {type.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="priority"
                    label="Priorité"
                    rules={[{ required: true, message: 'La priorité est requise' }]}
                  >
                    <Select placeholder="Sélectionner une priorité">
                      <Option value="medium">Normal</Option>
                      <Option value="high">Élevée</Option>
                      <Option value="urgent">Urgente</Option>
                      <Option value="low">Basse</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="title"
                label="Titre de la mission"
                rules={[{ required: true, message: 'Le titre est requis' }]}
              >
                <Input placeholder="Ex: Livraison Lyon Centre" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
              >
                <TextArea 
                  placeholder="Description détaillée de la mission..." 
                  rows={3} 
                />
              </Form.Item>

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="driver_id"
                    label="Chauffeur assigné"
                  >
                    <Select 
                      placeholder="Sélectionner un chauffeur"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      {availableDrivers.map((driver: any) => (
                        <Option key={driver.id} value={driver.id}>
                          {driver.first_name} {driver.last_name} ({driver.license_number})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="vehicle_id"
                    label="Véhicule assigné"
                  >
                    <Select 
                      placeholder="Sélectionner un véhicule"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      {availableVehicles.map((vehicle: any) => (
                        <Option key={vehicle.id} value={vehicle.id}>
                          {vehicle.license_plate} - {vehicle.brand} {vehicle.model}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="Adresses et planning" key="addresses">
              <Form.Item
                name="pickup_address"
                label="Adresse de départ"
              >
                <Input placeholder="Adresse complète de départ" />
              </Form.Item>

              <Form.Item
                name="delivery_address"
                label="Adresse de livraison"
              >
                <Input placeholder="Adresse complète de livraison" />
              </Form.Item>

              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    name="scheduled_pickup"
                    label="Départ prévu"
                  >
                    <DatePicker 
                      showTime
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY HH:mm"
                      placeholder="Sélectionner date et heure"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="scheduled_delivery"
                    label="Arrivée prévue"
                  >
                    <DatePicker 
                      showTime
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY HH:mm"
                      placeholder="Sélectionner date et heure"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Durée estimée (min)"
                    name="estimated_duration"
                  >
                    <InputNumber 
                      style={{ width: '100%' }}
                      placeholder="120"
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>

            {selectedMissionType && selectedMissionType.custom_fields?.length > 0 && (
              <TabPane tab="Champs personnalisés" key="custom">
                <CustomFieldRenderer fields={selectedMissionType.custom_fields} />
              </TabPane>
            )}

            <TabPane tab="Notes" key="notes">
              <Form.Item
                label="Notes additionnelles"
                name="notes"
              >
                <TextArea 
                  placeholder="Notes, instructions spéciales, commentaires..." 
                  rows={5} 
                />
              </Form.Item>
            </TabPane>
          </Tabs>

          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createMissionMutation.isPending || updateMissionMutation.isPending}
              >
                {editingMission ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminMissions;